import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  offset,
  shift,
  type Placement,
} from '@floating-ui/react-dom';

// Global replacement for the browser's native `title` tooltips.
//
// The app has ~260 `title="…"` attributes (icon buttons, truncated names,
// badges…). Rather than wrap each in `@ui/tooltip`, this installs one set
// of delegated listeners: when the mouse enters an element that carries a
// `title`, the attribute is parked in `data-title-tooltip` (which suppresses
// the native OS tooltip) and a styled tooltip — same look as `@ui/tooltip`
// (bg-toast, rounded, arrow, fade) — is shown instead. On leave the `title`
// is put back, so markup, tests and assistive tech see the element exactly
// as authored the rest of the time.
//
// Mouse only, like `@ui/tooltip`: touch devices never showed native titles,
// and keyboard focus doesn't trigger them either.

const SHOW_DELAY = 300;
// moving straight from one titled element to the next skips the delay
const WARM_WINDOW = 300;
const PARKED_ATTR = 'data-title-tooltip';
const SELECTOR = `[title]:not([title='']), [${PARKED_ATTR}]`;

let tip: HTMLDivElement | null = null;
let arrowEl: HTMLDivElement | null = null;
let labelEl: HTMLSpanElement | null = null;

let current: Element | null = null;
let showTimer: ReturnType<typeof setTimeout> | null = null;
let stopAutoUpdate: (() => void) | null = null;
let lastHiddenAt = 0;
let installed = false;

function ensureTip(): HTMLDivElement {
  if (tip) return tip;
  tip = document.createElement('div');
  tip.id = 'title-tooltip';
  tip.setAttribute('role', 'tooltip');
  tip.className =
    'pointer-events-none fixed left-0 top-0 z-tooltip max-w-240 break-words rounded bg-toast px-8 py-4 text-xs text-white shadow transition-[opacity,transform] duration-150 opacity-0 translate-y-4';
  tip.hidden = true;

  arrowEl = document.createElement('div');
  arrowEl.className = 'absolute h-8 w-8 rotate-45 bg-inherit';
  labelEl = document.createElement('span');
  labelEl.className = 'relative';

  tip.append(arrowEl, labelEl);
  document.body.append(tip);
  return tip;
}

function park(el: Element): string | null {
  const title = el.getAttribute('title');
  if (title != null) {
    el.setAttribute(PARKED_ATTR, title);
    el.removeAttribute('title');
    return title.trim() || null;
  }
  return el.getAttribute(PARKED_ATTR)?.trim() || null;
}

function restore(el: Element) {
  const parked = el.getAttribute(PARKED_ATTR);
  el.removeAttribute(PARKED_ATTR);
  // if React re-rendered a new title while hovered, keep the fresh one
  if (parked != null && !el.hasAttribute('title')) {
    el.setAttribute('title', parked);
  }
  if (el.getAttribute('aria-describedby') === 'title-tooltip') {
    el.removeAttribute('aria-describedby');
  }
}

async function position(target: Element) {
  if (!tip || !arrowEl) return;
  const {x, y, placement, middlewareData} = await computePosition(target, tip, {
    strategy: 'fixed',
    placement: 'top',
    middleware: [
      offset(10),
      flip({fallbackPlacements: ['bottom', 'right', 'left']}),
      shift({padding: 6}),
      arrow({element: arrowEl}),
    ],
  });
  Object.assign(tip.style, {left: `${x}px`, top: `${y}px`});

  const side = placement.split('-')[0] as 'top' | 'bottom' | 'left' | 'right';
  const staticSide = {top: 'bottom', bottom: 'top', left: 'right', right: 'left'}[
    side
  ] as Placement;
  const {x: ax, y: ay} = middlewareData.arrow ?? {};
  Object.assign(arrowEl.style, {
    left: ax != null ? `${ax}px` : '',
    top: ay != null ? `${ay}px` : '',
    right: '',
    bottom: '',
    [staticSide]: '-4px',
  });
}

function show(target: Element, text: string) {
  const el = ensureTip();
  labelEl!.textContent = text;
  el.hidden = false;
  target.setAttribute('aria-describedby', 'title-tooltip');
  stopAutoUpdate?.();
  stopAutoUpdate = autoUpdate(target, el, () => void position(target));
  // commit the hidden-state styles first so the fade/slide-in transitions
  void el.offsetWidth;
  el.classList.remove('opacity-0', 'translate-y-4');
}

function hide() {
  if (showTimer) {
    clearTimeout(showTimer);
    showTimer = null;
  }
  stopAutoUpdate?.();
  stopAutoUpdate = null;
  if (current) {
    restore(current);
    current = null;
  }
  if (tip && !tip.hidden) {
    lastHiddenAt = Date.now();
    tip.classList.add('opacity-0', 'translate-y-4');
    tip.hidden = true;
  }
}

function onPointerOver(e: PointerEvent) {
  if (e.pointerType !== 'mouse') return;
  const target =
    e.target instanceof Element ? e.target.closest(SELECTOR) : null;
  if (target === current) return;
  hide();
  if (!target) return;

  const text = park(target);
  if (!text) {
    restore(target);
    return;
  }
  current = target;
  const warm = Date.now() - lastHiddenAt < WARM_WINDOW;
  showTimer = setTimeout(
    () => {
      showTimer = null;
      if (current === target && target.isConnected) show(target, text);
    },
    warm ? 0 : SHOW_DELAY,
  );
}

function onPointerOut(e: PointerEvent) {
  if (!current) return;
  const next = e.relatedTarget;
  if (next instanceof Node && current.contains(next)) return;
  hide();
}

export function installTitleTooltips() {
  if (installed || typeof document === 'undefined') return;
  installed = true;
  document.addEventListener('pointerover', onPointerOver, true);
  document.addEventListener('pointerout', onPointerOut, true);
  // any interaction or movement of the page dismisses it, like the native one
  document.addEventListener('pointerdown', hide, true);
  document.addEventListener('wheel', hide, {capture: true, passive: true});
  document.addEventListener('scroll', hide, true);
  document.addEventListener(
    'keydown',
    e => {
      if (e.key === 'Escape') hide();
    },
    true,
  );
  window.addEventListener('blur', hide);
}
